/**
 * Client-side view of the enquiry ingestion column catalogue.
 *
 * The catalogue itself lives in nodefork
 * (`src/constants/enquiry-ingestion-columns.ts`) and is fetched at page load, so
 * there is exactly one definition of what the sheet can carry. This module only
 * holds the types and the pure helpers (header matching, format checks) that the
 * template generator, the parser and the config modal share.
 */

export type EnquiryColumnType = 'text' | 'number' | 'date' | 'email' | 'phone' | 'select';

export interface EnquiryColumnDef {
  key: string;
  label: string;
  group: string;
  type: EnquiryColumnType;
  aliases?: string[];
  /** null = recognised in the sheet but not persisted by the ingestor yet. */
  writeTarget: string | null;
  description?: string;
}

export interface EnquiryColumnGroup {
  key: string;
  label: string;
}

export interface EnquiryImportConfig {
  enabledColumns: string[];
  mandatoryColumns: string[];
  updatedAt?: string;
  updatedByUserId?: number;
}

export interface EnquiryImportCatalog {
  groups: EnquiryColumnGroup[];
  columns: EnquiryColumnDef[];
  config: EnquiryImportConfig;
  /** True only for the `root` login — the column picker is hidden for everyone else. */
  canConfigure: boolean;
}

/** Columns whose value may be written as AUTO to let the server generate it. */
export const AUTO_COLUMN_KEYS = ['studentcode', 'studentcode2', 'username', 'password'];

export const buildColumnIndex = (columns: EnquiryColumnDef[]): Map<string, EnquiryColumnDef> =>
  new Map(columns.map(c => [c.key, c]));

/** Every accepted header spelling (lower-cased) -> canonical column key. */
export const buildHeaderIndex = (columns: EnquiryColumnDef[]): Map<string, string> => {
  const index = new Map<string, string>();
  for (const col of columns) {
    index.set(col.key, col.key);
    index.set(col.label.toLowerCase(), col.key);
    for (const alias of col.aliases ?? []) index.set(alias.toLowerCase(), col.key);
  }
  return index;
};

export const normalizeGender = (gender: string): string | null => {
  const g = (gender ?? '').trim().toUpperCase();
  if (!g) return null;
  if (g === 'M' || g === 'MALE') return 'M';
  if (g === 'F' || g === 'FEMALE') return 'F';
  if (g === 'O' || g === 'OTHER' || g === 'OTHERS') return 'O';
  return null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\s()+-]{7,20}$/;

/** Mirror of the server's `validateCellFormat` so both report the same message. */
export const validateCellFormat = (column: EnquiryColumnDef, value: string): string | null => {
  if (!value) return null;
  switch (column.type) {
    case 'email':
      return EMAIL_RE.test(value) ? null : 'Invalid email format';
    case 'phone':
      return PHONE_RE.test(value) ? null : 'Must be 7-20 digits';
    case 'number':
      return Number.isFinite(Number(value.replace(/,/g, ''))) ? null : 'Must be a number';
    case 'select':
      if (column.key === 'gender') {
        return normalizeGender(value) ? null : 'Gender must be M/F/O or Male/Female/Others';
      }
      return null;
    default:
      return null;
  }
};

/** Enabled columns in catalogue order. */
export const orderedEnabledColumns = (
  columns: EnquiryColumnDef[],
  config: EnquiryImportConfig,
): EnquiryColumnDef[] => {
  const enabled = new Set(config.enabledColumns);
  return columns.filter(c => enabled.has(c.key));
};

export const groupLabel = (groups: EnquiryColumnGroup[], key: string): string =>
  groups.find(g => g.key === key)?.label ?? key;
